import { Link } from "react-router-dom";
import { SEO, serviceSchema, breadcrumbSchema } from "@/components/seo";
import { CTASection } from "@/components/common/CTASection";
import { FiArrowRight, FiCheck, FiClock, FiHome, FiDollarSign, FiRefreshCw, FiAlertTriangle } from "@/lib/icons";
import { broker } from "@/config/broker.config";

const howItWorks = [
  {
    step: 1,
    title: "Peak debt period",
    description:
      "You borrow enough to cover your new purchase while still owning your existing property. This is the maximum loan amount.",
  },
  {
    step: 2,
    title: "Sell your property",
    description:
      "List and sell your existing property. The bridging period is typically 6-12 months.",
  },
  {
    step: 3,
    title: "End debt",
    description:
      "Once your property sells, the proceeds pay down the loan to your 'end debt' - the ongoing loan for your new home.",
  },
];

const whenToUse = [
  {
    icon: <FiHome className="size-6" />,
    title: "Found your dream home",
    description:
      "When you've found the perfect property but haven't sold your current home yet.",
  },
  {
    icon: <FiClock className="size-6" />,
    title: "Tight settlement timing",
    description:
      "When settlement dates don't align - your new purchase settles before your sale.",
  },
  {
    icon: <FiRefreshCw className="size-6" />,
    title: "Avoiding double moves",
    description:
      "Move directly from your old home to your new one without renting in between.",
  },
  {
    icon: <FiDollarSign className="size-6" />,
    title: "Auction purchases",
    description:
      "Bid at auction with confidence knowing you have finance arranged.",
  },
];

const costs = [
  {
    item: "Interest during bridging period",
    description: "Usually capitalised (added to loan) rather than paid monthly. Can add up quickly.",
  },
  {
    item: "Higher interest rates",
    description: "Bridging loans often have slightly higher rates than standard home loans.",
  },
  {
    item: "Setup fees",
    description: "Application fees, valuation costs, and legal fees for two properties.",
  },
  {
    item: "Potential extension fees",
    description: "If your property takes longer to sell, you may need to extend the bridging period.",
  },
];

const considerations = [
  {
    title: "What if my property doesn't sell?",
    content:
      "Lenders typically allow 6-12 months. If you can't sell in time, you may need to reduce the price, extend the loan (fees apply), or in worst case, potentially sell your new property. We'll ensure you have a realistic plan.",
  },
  {
    title: "How much can I borrow?",
    content:
      "Peak debt is typically calculated as: new purchase price + existing loan balance + costs. Your end debt must be serviceable based on your income. We'll model different scenarios with you.",
  },
  {
    title: "Do I need a deposit?",
    content:
      "Equity from your existing property typically acts as your deposit. If you have sufficient equity, you may not need additional cash for the new purchase.",
  },
  {
    title: "Sell first or buy first?",
    content:
      "Selling first removes the risk but means renting temporarily. Buying first (with bridging) lets you move once but carries the risk of overlapping ownership. We can discuss what suits your situation.",
  },
];

const alternatives = [
  {
    title: "Subject to sale",
    description:
      "Make your offer conditional on selling your existing property. Less risky but may be less attractive to sellers.",
  },
  {
    title: "Extended settlement",
    description:
      "Negotiate a longer settlement period on your purchase to give time to sell your current home.",
  },
  {
    title: "Simultaneous settlement",
    description:
      "Coordinate both transactions to settle on the same day. Requires careful timing and coordination.",
  },
  {
    title: "Rent first",
    description:
      "Sell your property, rent temporarily, then buy. Eliminates bridging risk but involves two moves.",
  },
];

export function BridgingLoansPage() {
  return (
    <>
      <SEO
        title="Bridging Loans Brisbane, Springfield & Logan"
        description="Bridging finance to buy your next home before selling in Brisbane, Springfield, Logan or Ipswich. Expert guidance on timing, costs and risk."
        url="/home-loans/bridging"
        keywords={[
          "bridging loan Brisbane",
          "bridging finance Springfield",
          "bridging home loan",
          "buy before sell loan",
        ]}
        jsonLd={[
          serviceSchema({
            name: "Bridging Loans",
            description:
              "Bridging finance to buy your next home before selling your existing one — closed and open bridging structures.",
            url: "/home-loans/bridging",
            serviceType: "Bridging Loan",
          }),
          breadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Home Loans", url: "/home-loans" },
            { name: "Bridging", url: "/home-loans/bridging" },
          ]),
        ]}
      />

      {/* Hero */}
      <section className="bg-cream py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="max-w-3xl">
            <Link
              to="/home-loans"
              className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-6 transition-colors"
            >
              <FiArrowRight className="size-4 mr-2 rotate-180" />
              Home Loans
            </Link>
            <p className="section-label mb-4">Bridging Loans</p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6">
              Buy your next home before selling your current one
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              A bridging loan lets you purchase a new property while you're still
              selling your existing home. It bridges the gap between buying and
              selling, so you don't miss out on your dream home.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full font-medium hover:bg-primary/90 transition-colors"
              >
                Discuss Your Situation
                <FiArrowRight className="size-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">The Process</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              How bridging loans work
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {howItWorks.map((item) => (
              <div
                key={item.step}
                className="bg-white rounded-2xl p-6 border border-border/50"
              >
                <div className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-serif text-lg mb-4">
                  {item.step}
                </div>
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>

          {/* Visual diagram */}
          <div className="mt-12 bg-muted/30 rounded-2xl p-6 md:p-8">
            <div className="grid md:grid-cols-3 gap-6 items-center">
              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-2">Existing Property</p>
                <div className="bg-white rounded-xl p-4 border border-border">
                  <p className="font-semibold text-lg">$800,000</p>
                  <p className="text-xs text-muted-foreground">Value</p>
                  <p className="font-medium text-sm mt-2">$300,000</p>
                  <p className="text-xs text-muted-foreground">Owing</p>
                </div>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-2">Peak Debt</p>
                <div className="bg-primary text-primary-foreground rounded-xl p-4">
                  <p className="font-semibold text-lg">$1,100,000</p>
                  <p className="text-xs text-primary-foreground/90">
                    New purchase + existing loan
                  </p>
                </div>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-2">End Debt</p>
                <div className="bg-white rounded-xl p-4 border border-border">
                  <p className="font-semibold text-lg">$600,000</p>
                  <p className="text-xs text-muted-foreground">
                    After sale proceeds applied
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* When to use */}
      <section className="py-16 md:py-20 bg-cream">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">When It Makes Sense</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              When to consider bridging finance
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whenToUse.map((item) => (
              <div
                key={item.title}
                className="bg-white rounded-2xl p-6 border border-border/50 text-center"
              >
                <div className="p-3 bg-accent/20 rounded-xl inline-flex mb-4 text-primary">
                  {item.icon}
                </div>
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Costs */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="section-label mb-4">Costs to Consider</p>
              <h2 className="font-serif text-3xl md:text-4xl mb-6">
                Understand the costs involved
              </h2>
              <p className="text-muted-foreground mb-6">
                Bridging loans come with additional costs compared to a standard
                home loan. It's important to factor these in when deciding if
                bridging is right for you.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                <FiAlertTriangle className="size-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-900">Important</p>
                  <p className="text-sm text-amber-700">
                    The longer your property takes to sell, the more interest
                    you'll pay. Have a realistic timeline and pricing strategy.
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              {costs.map((cost) => (
                <div
                  key={cost.item}
                  className="bg-white rounded-xl p-4 border border-border/50"
                >
                  <h3 className="font-semibold mb-1">{cost.item}</h3>
                  <p className="text-sm text-muted-foreground">{cost.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ style considerations */}
      <section className="py-16 md:py-20 bg-primary text-primary-foreground">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">
              Common Questions
            </p>
            <h2 className="font-serif text-3xl md:text-4xl">
              Things you should know
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {considerations.map((item) => (
              <div
                key={item.title}
                className="bg-white/10 rounded-2xl p-6"
              >
                <h3 className="font-semibold mb-3">{item.title}</h3>
                <p className="text-sm text-primary-foreground/80">{item.content}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Alternatives */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">Alternatives</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              Other options to consider
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Bridging isn't always the best solution. Here are some alternatives
              we can discuss based on your situation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {alternatives.map((alt) => (
              <div
                key={alt.title}
                className="bg-muted/30 rounded-2xl p-6"
              >
                <h3 className="font-semibold mb-2">{alt.title}</h3>
                <p className="text-sm text-muted-foreground">{alt.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why work with us */}
      <section className="py-16 md:py-20 bg-cream">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="section-label mb-4">Expert Guidance</p>
              <h2 className="font-serif text-3xl md:text-4xl mb-6">
                Bridging loan specialists
              </h2>
              <p className="text-muted-foreground mb-8">
                Bridging loans require careful planning. We'll help you understand
                the true costs, manage the risks, and find the right lender for
                your situation.
              </p>
              <ul className="space-y-4">
                {[
                  "Calculate your peak and end debt scenarios",
                  "Compare bridging products from multiple lenders",
                  "Help you develop a realistic sale timeline",
                  "Explore alternatives if bridging isn't ideal",
                  "Manage the process from application to settlement",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <FiCheck className="size-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-3xl p-8 border border-border/50">
              <p className="text-sm text-muted-foreground mb-6">
                Example bridging scenario
              </p>
              <div className="space-y-4">
                <div className="flex justify-between py-3 border-b border-border">
                  <span className="text-muted-foreground">New purchase</span>
                  <span className="font-semibold">$1,000,000</span>
                </div>
                <div className="flex justify-between py-3 border-b border-border">
                  <span className="text-muted-foreground">Current loan</span>
                  <span className="font-semibold">$300,000</span>
                </div>
                <div className="flex justify-between py-3 border-b border-border">
                  <span className="text-muted-foreground">Costs & buffer</span>
                  <span className="font-semibold">$50,000</span>
                </div>
                <div className="flex justify-between py-3 bg-primary/5 rounded-lg px-3">
                  <span className="font-medium">Peak debt</span>
                  <span className="font-semibold text-primary">$1,350,000</span>
                </div>
                <div className="flex justify-between py-3 border-b border-border">
                  <span className="text-muted-foreground">Current property sale</span>
                  <span className="font-semibold">$800,000</span>
                </div>
                <div className="flex justify-between py-3 bg-accent/20 rounded-lg px-3">
                  <span className="font-medium">End debt</span>
                  <span className="font-semibold text-primary">$550,000</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        label="Need bridging finance?"
        title="Let's discuss if bridging is right for you"
        description="Book a free consultation and we'll assess your situation and options."
        primaryAction={{ label: "Get Started", href: "/contact" }}
        secondaryAction={{
          label: "Call Us",
          href: `tel:${broker.phone.replace(/\s/g, "")}`,
        }}
      />
    </>
  );
}
